import { useMemo, useState } from 'react';
import DataTable from '../../../../reusable/DataTable/DataTable';
import StatusChip from '../../../../reusable/StatusChip/StatusChip';
import CopyButton from '../../../../reusable/CopyButton/CopyButton';
import Button from '../../../../reusable/Button/Button';
import { useGetAffiliateCodesQuery } from '../../../../store/api/affiliateApi';
import { debounce } from '../../../../utilities/debounce';
import { NA } from '../../../../utilities/format';
import { PAGE_SIZE_OPTIONS, SEARCH_DELAY, formatDateTime, percent } from '../format';
import CodeDetailsDialog from './CodeDetailsDialog';
import { t } from '../../../../i18n/i18n';

/** Live writes a zero commission as "N/A" too — it never sends one on purpose. */
const commission = (value) => (value ? percent(value) : NA);

/**
 * Affiliate Marketing — Affiliate Codes. Live's code list
 * (`Dashhboard/AffiliateTable.jsx` on main): every code with its terms, links
 * and results, from `/affiliate/v2/code/list/`. "View Details" opens the
 * code's referrals and the countries they came from.
 */
export default function AffiliateCodes() {
  const [params, setParams] = useState({ page: 1, page_size: PAGE_SIZE_OPTIONS[0], search: '' });
  const [openCode, setOpenCode] = useState(null);

  const onSearch = useMemo(
    () => debounce((value) => setParams((prev) => ({ ...prev, search: value.trim(), page: 1 })), SEARCH_DELAY),
    []
  );

  const { data, isFetching, isError } = useGetAffiliateCodesQuery({
    page: params.page,
    page_size: params.page_size,
    ...(params.search && { search: params.search }),
  });

  const rows = data?.results ?? [];
  const total = data?.count ?? rows.length;

  const columns = useMemo(
    () => [
      {
        key: 'coupon_is_active',
        header: 'Status',
        width: 10,
        value: (r) => (r.coupon_is_active ? t('Active') : t('Deactivated')),
        render: (r) =>
          r.coupon_is_active ? (
            <StatusChip>{t('Active')}</StatusChip>
          ) : (
            <StatusChip tone="danger" size="sm">
              {t('Deactivated')}
            </StatusChip>
          ),
      },
      { key: 'created', header: 'Date', width: 14, value: (r) => formatDateTime(r.created) },
      { key: 'percentage', header: 'Commission %', width: 10, value: (r) => commission(r.percentage) },
      { key: 'repeat_percent', header: '% (Repeat)', width: 9, value: (r) => percent(r.repeat_percent) },
      {
        key: 'code',
        header: 'Code',
        width: 12,
        value: (r) => r.code ?? NA,
        tone: () => 'primary',
        render: (r) =>
          r.code ? (
            <span className="aff-copy-cell">
              <span>{r.code}</span>
              <CopyButton value={r.code} label="Affiliate code" />
            </span>
          ) : (
            NA
          ),
      },
      {
        key: 'url',
        header: 'URL',
        width: 18,
        value: (r) => r.url ?? NA,
        render: (r) =>
          r.url ? (
            <span className="aff-copy-cell">
              <a className="aff-link" href={r.url} target="_blank" rel="noopener noreferrer" title={r.url}>
                {r.url}
              </a>
              <CopyButton value={r.url} label="Affiliate link" />
            </span>
          ) : (
            NA
          ),
      },
      { key: 'pushed_leads', header: 'Pushed Leads', width: 9, value: (r) => r.pushed_leads || 0 },
      { key: 'conversion', header: 'Conversion', width: 9, value: (r) => r.conversion || 0 },
      {
        key: 'referred',
        header: 'Referred List',
        width: 10,
        searchable: false,
        render: (r) => (
          <Button
            variant="pill"
            height={28.8}
            paddingInline={14}
            disabled={r.coupon_id == null}
            onClick={() => setOpenCode(r)}
          >
            {t('View Details')}
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        icon="affiliate-signups"
        title="Affiliate Codes"
        count={isFetching && !data ? '—' : `${total}`}
        columns={columns}
        rows={rows}
        getRowId={(r, i) => r.coupon_id ?? r.code ?? i}
        minWidth={1180}
        total={total}
        page={params.page}
        onPageChange={(page, size) => setParams((prev) => ({ ...prev, page, page_size: size ?? prev.page_size }))}
        pageSize={params.page_size}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        loading={isFetching}
        searchPlaceholder="Search codes"
        onSearch={onSearch}
        emptyMessage={
          isError
            ? t('Could not load your affiliate codes')
            : params.search
              ? t('No codes match “{{search}}”', { search: params.search })
              : t('No affiliate codes yet')
        }
      />

      <CodeDetailsDialog code={openCode} onClose={() => setOpenCode(null)} />
    </>
  );
}
